import { Flag, Globe2, Percent, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import {
  Users, UserCheck, FileCheck2, FileClock, FileX2, Building2, Network,
  ShieldCheck, Package, ClipboardList, AlertTriangle, TrendingUp, PackageCheck, ChevronRight,
  Route, Gauge, Fuel,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { platformStyle } from "../lib/ridersPlatform";

const KPI = ({ icon: Icon, label, value, sub, cls, testid, onClick }) => {
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`rounded-2xl border p-4 bg-white ${cls || "border-slate-200"} ${clickable ? "cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-blue-400/50" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 font-mono-nums text-2xl font-extrabold text-slate-800">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
    </div>
  );
};

const DOC_COLORS = { valid: "#10B981", expiring: "#F59E0B", expired: "#EF4444" };

const SaudizationKPI = ({ d, isManager, onOpen }) => {
  const [target, setTarget] = useState(d.saudization_target || 0);
  const pct = d.saudization_pct ?? 0;
  const ok = target ? pct >= target : null;
  const edit = async (e) => {
    e.stopPropagation();
    const v = window.prompt("نسبة السعودة المطلوبة حسب نطاقك في منصة قوى (%)", String(target || ""));
    if (v === null) return;
    const n = parseFloat(v);
    if (Number.isNaN(n)) { toast.error("أدخل رقماً صحيحاً"); return; }
    try { const r = await api.setSaudizationTarget(n); setTarget(r.target); toast.success("تم حفظ النسبة المطلوبة"); } catch { toast.error("تعذّر الحفظ"); }
  };
  const cls = ok === null ? "border-slate-200 bg-white" : ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50";
  return (
    <button type="button" data-testid="ov-saudization" onClick={onOpen} className={`rounded-2xl border p-4 text-right transition hover:-translate-y-0.5 hover:shadow-md ${cls}`}>
      <div className="flex items-center justify-between"><span className="text-sm text-slate-500">نسبة السعودة <span className="text-[11px] text-slate-400">حسب نطاق قوى</span></span>
        {isManager ? <span role="button" tabIndex={0} data-testid="ov-saudization-edit" onClick={edit} className="rounded p-0.5 text-slate-400 hover:bg-white hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></span> : <Percent className="h-4 w-4 text-slate-400" />}</div>
      <div className={`mt-2 font-mono-nums text-2xl font-extrabold ${ok === null ? "text-slate-800" : ok ? "text-emerald-700" : "text-red-600"}`}>{pct}%</div>
      <div className="mt-0.5 text-xs text-slate-500">{target ? `المطلوب في قوى ${target}% · ${ok ? "ضمن النطاق ✓" : "أقل من المطلوب"}` : "اضغط ✎ وأدخل نسبة نطاقك في قوى"}</div>
    </button>
  );
};

const NAT_COLORS = ["#059669", "#2563EB", "#D97706", "#DB2777", "#7C3AED", "#0D9488", "#EA580C", "#64748B"];

export const OverviewPanel = ({ isManager, onNav }) => {
  const { t, dir } = useLang();
  const nav = (a) => onNav && onNav(a);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dist, setDist] = useState(null);
  const [monthIdx, setMonthIdx] = useState({});
  const fmtMonth = (ym) => { if (!ym) return ""; const [y, m] = ym.split("-"); return `${m}/${y}`; };

  useEffect(() => {
    let ok = true;
    api.overview().then((res) => ok && setD(res)).catch(() => {}).finally(() => ok && setLoading(false));
    // Current-month riders distances & fuel summary (editor-only; silently skipped otherwise).
    api.distancesOverview("cumulative", null, null, null, "هنجرستيشن", "month")
      .then((res) => ok && setDist(res)).catch(() => {});
    return () => { ok = false; };
  }, []);

  if (loading) return <div className="py-16 text-center text-slate-400" data-testid="overview-loading">{t("dash.overview.loading")}</div>;
  if (!d) return <div className="py-16 text-center text-slate-400">{t("dash.overview.loadFail")}</div>;

  const docPie = [
    { name: t("dash.overview.pieValid"), key: "valid", value: d.docs_valid },
    { name: t("dash.overview.pieExpiring"), key: "expiring", value: d.docs_expiring },
    { name: t("dash.overview.pieExpired"), key: "expired", value: d.docs_expired },
  ].filter((x) => x.value > 0);

  return (
    <div className="space-y-6" data-testid="overview-panel" dir={dir}>
      {/* Attention banner */}
      <div className={`rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap ${d.attention_count > 0 ? "bg-gradient-to-l from-amber-500 to-orange-500 text-white" : "bg-gradient-to-l from-emerald-600 to-teal-600 text-white"}`}>
        <div className="flex items-center gap-3">
          {d.attention_count > 0 ? <AlertTriangle className="h-7 w-7" /> : <ShieldCheck className="h-7 w-7" />}
          <div>
            <div className="font-heading text-xl font-extrabold">
              {d.attention_count > 0 ? t("dash.overview.attention", { n: d.attention_count }) : t("dash.overview.allGood")}
            </div>
            <div className="text-sm opacity-90">
              {d.attention_count > 0 ? t("dash.overview.attentionSub") : t("dash.overview.allGoodSub")}
            </div>
          </div>
        </div>
        <TrendingUp className="h-10 w-10 opacity-40" />
      </div>

      {/* Employees + docs KPIs */}
      <div>
        <h3 className="font-heading text-sm font-bold text-slate-500 mb-3">{t("dash.overview.empDocsHead")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          <KPI testid="ov-employees" onClick={() => nav("employees")} icon={Users} label={t("dash.overview.employees")} value={d.employees_total} sub={t("dash.overview.nActive", { n: d.employees_active })} cls="border-blue-100 bg-blue-50/40" />
          <KPI testid="ov-active" onClick={() => nav("employees")} icon={UserCheck} label={t("dash.overview.active")} value={d.employees_active} />
          <KPI testid="ov-saudi" onClick={() => nav("saudi-files")} icon={Flag} label="السعوديون (نشط)" value={d.saudi_active ?? 0} sub={`${(d.saudi_total ?? 0) - (d.saudi_active ?? 0)} غير نشط`} cls="border-emerald-200 bg-emerald-50/50" />
          <KPI testid="ov-residents" onClick={() => nav("residents")} icon={Globe2} label="المقيمون" value={d.resident_total ?? 0} sub={`${d.resident_active ?? 0} نشط`} cls="border-sky-200 bg-sky-50/50" />
          <SaudizationKPI d={d} isManager={isManager} onOpen={() => nav("saudi-files")} />
          <KPI testid="ov-docs-total" onClick={() => nav("employees")} icon={ClipboardList} label={t("dash.overview.docsTotal")} value={d.docs_total} />
          <KPI testid="ov-valid" onClick={() => nav("docs-valid")} icon={FileCheck2} label={t("dash.overview.valid")} value={d.docs_valid} cls="border-emerald-100 bg-emerald-50/40" />
          <KPI testid="ov-expiring" onClick={() => nav("docs-expiring")} icon={FileClock} label={t("dash.overview.expiring")} value={d.docs_expiring} cls="border-amber-100 bg-amber-50/40" />
          <KPI testid="ov-expired" onClick={() => nav("docs-expired")} icon={FileX2} label={t("dash.overview.expired")} value={d.docs_expired} cls="border-red-100 bg-red-50/40" />
        </div>
      </div>

      {/* Org structure + assets + records */}
      <div>
        <h3 className="font-heading text-sm font-bold text-slate-500 mb-3">{t("dash.overview.structHead")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KPI testid="ov-branches" onClick={() => nav("branches")} icon={Building2} label={t("dash.overview.branches")} value={d.branches_count} />
          {isManager && <KPI testid="ov-departments" onClick={() => nav("departments")} icon={Network} label={t("dash.overview.departments")} value={d.departments_count} />}
          {isManager && <KPI testid="ov-users" onClick={() => nav("users")} icon={ShieldCheck} label={t("dash.overview.users")} value={d.users_count} />}
          <KPI testid="ov-assets" onClick={() => nav("custody")} icon={Package} label={t("dash.overview.custody")} value={d.assets_total} sub={t("dash.overview.assigned", { n: d.assets_assigned })} />
          {(() => {
            const byPlatMonths = d.completed_orders_by_platform_months || {};
            const byPlat = d.completed_orders_by_platform || {};
            const order = ["هنجرستيشن", "نينجا", "كيتا طلبات", "كيتا شرائح"];
            const allPlats = Object.keys(byPlatMonths).length
              ? byPlatMonths
              : Object.fromEntries(Object.keys(byPlat).map((p) => [p, [{ month: null, orders: byPlat[p] }]]));
            const names = [...order.filter((p) => p in allPlats), ...Object.keys(allPlats).filter((p) => !order.includes(p))];
            if (names.length === 0) {
              return <KPI testid="ov-completed-orders" onClick={() => nav("riders")} icon={PackageCheck} label={t("dash.overview.completedOrders")} value={(d.completed_orders || 0).toLocaleString("en-US")} cls="border-blue-100 bg-blue-50/40" />;
            }
            return names.map((p) => {
              const st = platformStyle(p);
              const months = allPlats[p] || [];
              const idx = Math.min(monthIdx[p] || 0, Math.max(0, months.length - 1));
              const cur = months[idx] || { month: null, orders: byPlat[p] || 0 };
              const multi = months.length > 1;
              const advance = () => { if (multi) setMonthIdx((m) => ({ ...m, [p]: (idx + 1) % months.length })); };
              return (
                <div key={p} data-testid={`ov-completed-${p}`} role="button" tabIndex={0}
                  onClick={() => nav("riders")}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav("riders"); } }}
                  className="cursor-pointer rounded-2xl border bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400/50 active:translate-y-0" style={{ borderColor: st.ring }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t("dash.overview.completedOrders")}</span>
                    {st.logo
                      ? <img src={st.logo} alt={p} className="h-5 w-5 rounded object-contain" />
                      : <PackageCheck className="h-4 w-4 text-slate-400" />}
                  </div>
                  <div className="mt-2 font-mono-nums text-2xl font-extrabold" style={{ color: st.text }}>{(cur.orders || 0).toLocaleString("en-US")}</div>
                  <div className="mt-0.5 flex items-center justify-between gap-1">
                    <span className="text-xs font-semibold" style={{ color: st.color }}>{p}</span>
                    {cur.month && (
                      <span className="font-mono-nums text-[11px] text-slate-400">
                        {idx === 0 ? "الحالي · " : ""}{fmtMonth(cur.month)}
                      </span>
                    )}
                  </div>
                  {multi && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); advance(); }} data-testid={`ov-completed-prev-${p}`}
                      className="mt-1 flex items-center gap-1 rounded text-[10px] text-slate-500 hover:text-blue-700">
                      <ChevronRight className="h-3 w-3" /> الشهر السابق
                    </button>
                  )}
                </div>
              );
            });
          })()}
          {isManager && <KPI testid="ov-records" onClick={() => nav("company")} icon={ClipboardList} label={t("dash.overview.records")} value={d.records_total} sub={d.records_expired + d.records_expiring > 0 ? t("dash.overview.needRenew", { n: d.records_expired + d.records_expiring }) : t("dash.overview.allValid")} />}
        </div>
      </div>

      {/* Riders distances & fuel (current month) — mirrored from the distances report */}
      {dist && (
        <div>
          <h3 className="text-sm font-bold text-slate-700 mb-2">المسافات والوقود {dist.month ? `(${fmtMonth(dist.month)})` : ""}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KPI testid="ov-total-km" onClick={() => nav("riders-distances")} icon={Route}
              label="إجمالي الكيلومترات"
              value={`${(dist?.summary?.total_km || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} كم`}
              cls="border-indigo-100 bg-indigo-50/40" />
            <KPI testid="ov-payable-km" onClick={() => nav("riders-distances")} icon={Gauge}
              label="الكيلومترات المستحقة"
              value={`${(dist?.summary?.payable_km || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} كم`}
              cls="border-emerald-100 bg-emerald-50/40" />
            <KPI testid="ov-fuel-cost" onClick={() => nav("riders-distances")} icon={Fuel}
              label="إجمالي تكلفة الوقود"
              value={`${((dist?.fuel_efficiency || []).reduce((a, e) => a + (e.fuel_cost || 0), 0)).toLocaleString("en-US", { maximumFractionDigits: 2 })} ⃁`}
              cls="border-green-100 bg-green-50/40" />
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-700 mb-2">{t("dash.overview.docStatus")}</p>
          {docPie.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-slate-400 text-sm">{t("dash.overview.noDatedDocs")}</div>
          ) : (
            <ResponsiveContainer width="100%" height={224}>
              <PieChart>
                <Pie data={docPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3} cursor="pointer" onClick={(_, i) => docPie[i] && nav(`docs-${docPie[i].key}`)}>
                  {docPie.map((e) => <Cell key={e.key} fill={DOC_COLORS[e.key]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-center gap-4 text-xs">
            <button type="button" onClick={() => nav("docs-valid")} data-testid="ov-pie-valid" className="flex items-center gap-1 hover:underline"><i className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: DOC_COLORS.valid }} /> {t("dash.overview.pieValid")} {d.docs_valid}</button>
            <button type="button" onClick={() => nav("docs-expiring")} data-testid="ov-pie-expiring" className="flex items-center gap-1 hover:underline"><i className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: DOC_COLORS.expiring }} /> {t("dash.overview.pieExpiring")} {d.docs_expiring}</button>
            <button type="button" onClick={() => nav("docs-expired")} data-testid="ov-pie-expired" className="flex items-center gap-1 hover:underline"><i className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: DOC_COLORS.expired }} /> {t("dash.overview.pieExpired")} {d.docs_expired}</button>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-700 mb-2">{t("dash.overview.byBranch")}</p>
          {(!d.branch_distribution || d.branch_distribution.length === 0) ? (
            <div className="h-56 flex items-center justify-center text-slate-400 text-sm">{t("dash.overview.noBranches")}</div>
          ) : (
            <div dir="ltr"><ResponsiveContainer width="100%" height={224}>
              <BarChart data={d.branch_distribution} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="branch" tick={{ fontSize: 11 }} interval={0} height={30} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name={t("dash.overview.employeesLegend")} fill="#2563EB" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(x) => x?.branch && x.branch !== "غير محدد" && nav(`branch:${x.branch}`)} />
              </BarChart>
            </ResponsiveContainer></div>
          )}
        </div>
      </div>

      {(d.nationality_distribution || []).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="ov-nationality-chart">
          <p className="mb-2 text-sm font-semibold text-slate-700">الموظفون النشطون حسب الجنسية</p>
          <div dir="ltr">
          <ResponsiveContainer width="100%" height={Math.max(160, d.nationality_distribution.length * 34)}>
            <BarChart data={d.nationality_distribution} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} reversed />
              <YAxis type="category" dataKey="nationality" tick={{ fontSize: 12, fill: "#334155" }} width={110} orientation="right" interval={0} />
              <Tooltip />
              <Bar dataKey="count" name="عدد الموظفين" radius={[6, 0, 0, 6]} cursor="pointer" onClick={(x) => nav(`nat:${x.nationality}`)}>
                {d.nationality_distribution.map((x, i) => <Cell key={x.nationality} fill={NAT_COLORS[i % NAT_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          </div>
        </div>
      )}

      {(d.custom_reports || []).length > 0 && (
        <div data-testid="overview-custom-reports">
          <h3 className="font-heading font-bold text-slate-800 mb-2">{t("dash.overview.platformReports")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {d.custom_reports.map((r) => (
              <div key={r.def_id} data-testid={`overview-report-${r.def_id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading font-bold text-slate-900">{r.name}</span>
                  <span className="text-xs text-slate-400">{r.latest_date || "—"} · {r.row_count} {t("dash.overview.recordSuffix")}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {r.kpis.length === 0 ? (
                    <div className="col-span-4 text-xs text-slate-400 text-center py-2">{t("dash.overview.noKpis")}</div>
                  ) : r.kpis.map((k) => (
                    <div key={k.key} className="rounded-xl bg-slate-50 border border-slate-100 p-2.5 text-center">
                      <div className="text-[11px] text-slate-400 truncate">{k.label}</div>
                      <div className="font-heading font-bold text-slate-800 font-mono-nums">{Number(k.sum || 0).toLocaleString("en-US")}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
